<template>
  <section>
    <transition name="fade">
      <section style="position: absolute; width: 100%;">
        <v-container v-if="preloader">
          <div class="text-center">
            <v-progress-circular indeterminate></v-progress-circular>
          </div>
        </v-container>
      </section>
    </transition>

    <div :class="preloader ? 'hidden-block' : 'show-block'" class="row custom-appointments">
      <div class="col-12 mt-0 pt-0">

        <div class="templates-toolbar">
          <h3 class="templates-title">Шаблоны компетенций</h3>
          <div class="templates-toolbar__actions">
            <v-btn text :loading="exportLoading" @click="exportTemplates">
              <i class="ti-export" />&nbsp;Экспорт
            </v-btn>
            <v-btn text :loading="importLoading" @click="triggerImportFileSelect">
              <i class="ti-import" />&nbsp;Импорт
            </v-btn>
            <input
              ref="templatesImportInput"
              type="file"
              accept="application/json"
              style="display: none;"
              @change="onImportFileSelected"
            >
            <v-btn color="pink" class="templates-new-btn" @click="startNewTemplate">
              + Новый шаблон
            </v-btn>
          </div>
        </div>

        <div v-if="errorText" class="error-text mb-3">{{ errorText }}</div>
        <div v-if="infoText" class="info-text mb-3">{{ infoText }}</div>

        <!-- Список шаблонов -->
        <div v-if="!editing" class="container-color templates-list">
          <div v-if="!templates.length" class="empty-text">Шаблонов пока нет</div>

          <div v-for="tpl in templates" :key="tpl.id" class="template-row">
            <div class="template-row__info">
              <b>{{ tpl.title }}</b>
              <span class="template-row__meta">
                {{ tpl.itemsCount }} элементов
                <template v-if="tpl.shared"> · общедоступный</template>
                <template v-if="!tpl.isOwn"> · автор: {{ tpl.ownerName }}</template>
              </span>
            </div>
            <div class="template-row__actions">
              <v-btn small text @click="openTemplate(tpl)">Открыть</v-btn>
              <v-btn v-if="tpl.isOwn" small text color="error" @click="confirmDelete(tpl)">Удалить</v-btn>
            </div>
          </div>
        </div>

        <!-- Редактор шаблона -->
        <div v-else class="container-color" style="position:relative; padding: 16px 30px 30px;">
          <div class="template-editor-head">
            <input
              v-model="editTitle"
              type="text"
              class="template-editor-title-input"
              placeholder="Название шаблона"
            >
            <label class="template-editor-shared">
              <input type="checkbox" v-model="editShared">
              Общедоступный (виден всем, кто может назначать компетенции)
            </label>

            <div class="template-editor-actions">
              <v-btn small text @click="cancelEdit">Отмена</v-btn>
              <v-btn small color="pink" :disabled="saving" @click="saveTemplate">
                {{ saving ? "Сохранение..." : "Сохранить" }}
              </v-btn>
            </div>
          </div>

          <div class="rights-tree-head">
            <div class="rights-tree-head__top">
              <span class="rights-tree-head__title">Hard Skills</span>
            </div>
            <div class="rights-tree-head__cols">
              <span
                v-for="btn in rightsButtons"
                :key="'headcol_' + btn.id"
                class="rights-tree-head__col"
                :title="btn.description"
              >
                {{ btn.description }}
              </span>
            </div>
          </div>

          <RightsTreeNode
            v-for="itemArea in hardSkillAreas"
            :key="itemArea.id"
            :node="itemArea"
            :level="1"
            :open-ids="arrayOpen"
            :rights-buttons="rightsButtons"
            :get-right="getRight"
            @toggle-open="openBlock"
            @toggle-open-deep="openBlockDeep"
            @toggle-right="toggleRight"
          />

          <div class="rights-tree-head" style="margin-top: 20px;">
            <div class="rights-tree-head__top">
              <span class="rights-tree-head__title">Soft Skills</span>
            </div>
          </div>

          <RightsTreeNode
            v-for="itemArea in softSkillAreas"
            :key="itemArea.id"
            :node="itemArea"
            :level="1"
            :open-ids="arrayOpen"
            :rights-buttons="rightsButtons"
            :get-right="getRight"
            @toggle-open="openBlock"
            @toggle-open-deep="openBlockDeep"
            @toggle-right="toggleRight"
          />
        </div>

      </div>
    </div>

    <ConfirmDialog
      v-if="templateToDelete"
      :visible="true"
      title="Удалить шаблон?"
      :text="'Шаблон «' + (templateToDelete && templateToDelete.title) + '» будет удалён без возможности восстановления.'"
      @confirm="doDelete"
      @cancel="templateToDelete = null"
    />

    <ConfirmDialog
      v-if="importPayload"
      :visible="true"
      title="Импорт шаблонов"
      :text="'Будет импортировано шаблонов: ' + importPayload.length + '. Каждый шаблон создастся как новый (владелец — вы), существующие шаблоны не изменятся.'"
      confirm-label="Импортировать"
      cancel-label="Отмена"
      @confirm="doImport"
      @cancel="importPayload = null"
    />
  </section>
</template>

<script>
import GlobalRequest from "@/plugins/globalRequest";
import RightsTreeNode from "@/components/RightsTreeNode.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

// соответствие ключа права битовой маске BaseRightEnum на бэкенде
const RIGHT_BITS = {
  canView: 1,
  canViewScore: 2,
  canPostAnswer: 4,
  canPostScore: 8,
  canViewReferenceAnswer: 128,
  assigned: 64,
};

export default {
  name: "CompetencyTemplates",
  components: {
    RightsTreeNode,
    ConfirmDialog,
  },
  data() {
    return {
      preloader: true,
      saving: false,
      errorText: "",
      infoText: "",

      templates: [],
      structure: { hardSkills: [], softSkills: [] },

      editing: false,
      editTemplateId: null,
      editTitle: "",
      editShared: false,
      arrayOpen: [],
      // elementId -> rightsMask (число, битовая маска)
      rightsByElementId: {},

      templateToDelete: null,

      exportLoading: false,
      importLoading: false,
      importPayload: null,

      rightsButtons: [
        { id: 1, title: "canView", color: "#5d30b2", description: "просмотр компетенции" },
        { id: 2, title: "canViewScore", color: "#3091B2", description: "просмотр оценки" },
        { id: 3, title: "canPostScore", color: "#229863", description: "оценка компетенции" },
        { id: 4, title: "canPostAnswer", color: "#BB992F", description: "ответ на задание" },
        { id: 6, title: "canViewReferenceAnswer", color: "#a85c00", description: "просмотр эталонного ответа" },
        { id: 5, title: "assigned", color: "#B33C12", description: "назначить компетенцию" },
      ],
    };
  },
  computed: {
    hardSkillAreas() {
      return this.structure.hardSkills || [];
    },
    softSkillAreas() {
      return this.structure.softSkills || [];
    },
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      this.preloader = true;
      await Promise.all([this.loadTemplates(), this.loadStructure()]);
      this.preloader = false;
    },

    async loadTemplates() {
      const res = await GlobalRequest.request("/competency-templates", "GET");
      this.templates = res && res.success && Array.isArray(res.data) ? res.data : [];
    },

    async loadStructure() {
      const res = await GlobalRequest.request("/competency-templates/structure", "GET");
      this.structure = res && res.success && res.data ? res.data : { hardSkills: [], softSkills: [] };
    },

    startNewTemplate() {
      this.editTemplateId = null;
      this.editTitle = "";
      this.editShared = false;
      this.rightsByElementId = {};
      this.arrayOpen = [];
      this.editing = true;
      this.errorText = "";
      this.infoText = "";
    },

    async openTemplate(tpl) {
      this.errorText = "";
      this.infoText = "";
      this.preloader = true;

      const res = await GlobalRequest.request("/competency-templates/" + tpl.id, "GET");
      this.preloader = false;

      if (!res || !res.success) {
        this.errorText = "Не удалось загрузить шаблон";
        return;
      }

      const data = res.data;
      this.editTemplateId = data.id;
      this.editTitle = data.title;
      this.editShared = data.shared;
      this.arrayOpen = [];

      const map = {};
      (data.items || []).forEach((item) => {
        map[item.elementId] = item.rightsMask;
      });
      this.rightsByElementId = map;

      this.editing = true;
    },

    cancelEdit() {
      this.editing = false;
      this.editTemplateId = null;
    },

    openBlock(id) {
      var idx = this.arrayOpen.indexOf(id);
      if (idx !== -1) this.arrayOpen.splice(idx, 1);
      else this.arrayOpen.push(id);
    },

    openBlockDeep(ids, shouldOpen) {
      if (!ids || !ids.length) return;
      if (shouldOpen) {
        ids.forEach((id) => {
          if (!this.arrayOpen.includes(id)) this.arrayOpen.push(id);
        });
      } else {
        this.arrayOpen = this.arrayOpen.filter((id) => !ids.includes(id));
      }
    },

    // targetRights-совместимый геттер: право включено, если соответствующий бит установлен в маске элемента
    getRight(node, key) {
      const mask = this.rightsByElementId[node.id] || 0;
      const bit = RIGHT_BITS[key];
      return !!(mask & bit);
    },

    toggleRight(node, btn, descendants) {
      const bit = RIGHT_BITS[btn.title];
      const current = this.rightsByElementId[node.id] || 0;
      const turningOn = !(current & bit);

      this.setRightBit(node.id, bit, turningOn);

      // каскадируем то же состояние (вкл/выкл) вниз по всем потомкам
      if (descendants && descendants.length) {
        descendants.forEach((child) => this.setRightBit(child.id, bit, turningOn));
      }
    },

    setRightBit(elementId, bit, turnOn) {
      const current = this.rightsByElementId[elementId] || 0;
      const next = turnOn ? current | bit : current & ~bit;

      if (next === 0) {
        this.$delete(this.rightsByElementId, elementId);
      } else {
        this.$set(this.rightsByElementId, elementId, next);
      }
    },

    async saveTemplate() {
      this.errorText = "";
      this.infoText = "";

      if (!this.editTitle || !this.editTitle.trim()) {
        this.errorText = "Укажите название шаблона";
        return;
      }

      const items = Object.keys(this.rightsByElementId).map((elementId) => ({
        elementId: Number(elementId),
        rightsMask: this.rightsByElementId[elementId],
      }));

      if (!items.length) {
        this.errorText = "Отметьте хотя бы одно право хотя бы на одном элементе";
        return;
      }

      this.saving = true;

      const body = {
        title: this.editTitle.trim(),
        shared: this.editShared,
        items,
      };

      const url = this.editTemplateId
        ? "/competency-templates/" + this.editTemplateId
        : "/competency-templates";

      const res = await GlobalRequest.request(url, "POST", body);
      this.saving = false;

      if (!res || !res.success) {
        this.errorText = (res && res.error) || "Не удалось сохранить шаблон";
        return;
      }

      this.infoText = "Шаблон сохранён";
      this.editing = false;
      await this.loadTemplates();
    },

    confirmDelete(tpl) {
      this.templateToDelete = tpl;
    },

    async doDelete() {
      const tpl = this.templateToDelete;
      this.templateToDelete = null;
      if (!tpl) return;

      const res = await GlobalRequest.request("/competency-templates/" + tpl.id, "DELETE");
      if (!res || !res.success) {
        this.errorText = "Не удалось удалить шаблон";
        return;
      }

      this.infoText = "Шаблон удалён";
      await this.loadTemplates();
    },

    // Экспорт всех доступных шаблонов (свои + пошаренные) одним JSON-файлом — для бэкапа/переноса
    async exportTemplates() {
      this.errorText = "";
      this.infoText = "";
      this.exportLoading = true;

      try {
        const res = await GlobalRequest.request("/competency-templates/export", "GET");

        if (!res || !res.success) {
          this.errorText = (res && res.error) || "Не удалось экспортировать шаблоны";
          return;
        }

        const blob = new Blob([JSON.stringify(res.data, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const date = new Date().toISOString().slice(0, 10);

        link.href = url;
        link.download = `competency-templates-${date}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.infoText = "Шаблоны экспортированы";
      } catch (e) {
        this.errorText = "Ошибка при экспорте шаблонов";
      } finally {
        this.exportLoading = false;
      }
    },

    triggerImportFileSelect() {
      this.$refs.templatesImportInput.click();
    },

    async onImportFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      this.errorText = "";
      this.infoText = "";

      let data;
      try {
        const text = await file.text();
        data = JSON.parse(text);
      } catch (e) {
        this.errorText = "Файл не является корректным JSON";
        return;
      }

      this.importPayload = Array.isArray(data) ? data : [data];
    },

    async doImport() {
      const payload = this.importPayload;
      this.importPayload = null;
      if (!payload) return;

      this.importLoading = true;

      try {
        const res = await GlobalRequest.request("/competency-templates/import", "POST", payload);

        if (!res || !res.success) {
          this.errorText = (res && res.error) || "Не удалось импортировать шаблоны";
          return;
        }

        this.infoText = `Импортировано шаблонов: ${res.data.imported_count}`;
        await this.loadTemplates();
      } catch (e) {
        this.errorText = "Ошибка при импорте шаблонов";
      } finally {
        this.importLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.templates-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.templates-title {
  margin: 0;
}
.templates-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.templates-list {
  padding: 16px 30px;
}
.template-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.template-row__meta {
  color: #999;
  font-size: 12px;
  margin-left: 10px;
}
.template-row__actions {
  display: flex;
  gap: 6px;
}
.empty-text {
  color: #999;
  padding: 20px 0;
  text-align: center;
}
.error-text {
  color: #d32f2f;
}
.info-text {
  color: #2e7d32;
}
.template-editor-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}
.template-editor-title-input {
  flex: 1;
  min-width: 240px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
}
.template-editor-shared {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
}
.template-editor-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}
.rights-tree-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0 12px;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 10px;
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}
.rights-tree-head__top {
  display: flex;
  align-items: center;
}
.rights-tree-head__title {
  font-weight: 600;
  font-size: 16px;
}
.rights-tree-head__cols {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
.rights-tree-head__col {
  width: 48px;
  flex: 0 0 auto;
  font-size: 10px;
  line-height: 1.15;
  color: #777;
  text-align: center;
  white-space: normal;
  word-break: break-word;
}
</style>
